import { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import { notify, notifyError } from "../../../utils/toast";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import type {
  GetStoreListQuery,
  OrdersByStoreIdQuery,
  CancelOrderByAdminMutation,
  CancelOrderByAdminMutationVariables,
} from "../../../__generated__/graphql";

const ORDERS_BY_STORE_ID = gql(`
  query ordersByStoreId($storeId: Int!) {
    ordersByStoreId(storeId: $storeId) {
      id
      user {
        id
        name
        phone
      }
      isCanceled
      isPackConfirmed
      totalQuantity
      amount
      createdAt
    }
  }
`);

const CANCEL_ORDER_BY_ADMIN = gql(`
  mutation CancelOrderByAdmin($orderId: Int!, $cancelReason: String!) {
    cancelOrderByAdmin(orderId: $orderId, cancelReason: $cancelReason) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  NonNullable<GetStoreListQuery["getStoreList"]>["result"]
>[number];

export default function AdminCancelOrder() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  useScrollToDetailPanelOnSelect(selectedStore);

  const {
    data: storeData,
    loading: storeLoading,
    error: storeError,
  } = useQuery<GetStoreListQuery>(GET_STORE_LIST);

  const [
    getOrdersByStoreId,
    { data: ordersData, loading: ordersLoading, refetch: refetchOrders },
  ] = useLazyQuery<OrdersByStoreIdQuery>(ORDERS_BY_STORE_ID, {
    variables: { storeId: selectedStore?.id ?? 0 },
  });

  const [cancelOrder] = useMutation<
    CancelOrderByAdminMutation,
    CancelOrderByAdminMutationVariables
  >(CANCEL_ORDER_BY_ADMIN);

  useEffect(() => {
    if (selectedStore?.id) {
      getOrdersByStoreId({ variables: { storeId: selectedStore.id } });
    }
  }, [selectedStore?.id, getOrdersByStoreId]);

  const fullList = storeData?.getStoreList?.ok
    ? (storeData.getStoreList.result ?? [])
    : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const storeList = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    setSelectedOrderId(null);
    setCancelReason("");
  };

  const handleCancelOrder = async (orderId: number) => {
    if (!cancelReason.trim()) {
      notifyError("취소 사유를 입력해주세요.");
      return;
    }
    if (cancelReason.length < 4) {
      notifyError("취소 사유는 4글자 이상 입력해주세요.");
      return;
    }
    if (!window.confirm("해당 고객이 맞습니까?")) {
      return;
    }
    await cancelOrder({
      variables: { orderId, cancelReason },
      onCompleted: (res) => {
        if (res.cancelOrderByAdmin.ok) {
          notify("주문이 취소되었습니다.");
          setSelectedOrderId(null);
          setCancelReason("");
          refetchOrders();
        } else {
          notifyError(res.cancelOrderByAdmin.error ?? "취소에 실패했습니다.");
          refetchOrders();
        }
      },
      onError: () => {
        refetchOrders();
      },
    });
  };

  const orders = ordersData?.ordersByStoreId ?? [];

  if (storeLoading) {
    return (
      <div className="w-full p-4 flex justify-center py-8">
        <LoadingSpinnerInline />
      </div>
    );
  }
  if (storeError) {
    return (
      <div className="w-full p-4 text-red-600">에러: {storeError.message}</div>
    );
  }
  if (!storeData?.getStoreList?.ok) {
    return (
      <div className="w-full p-4 text-red-600">
        {storeData?.getStoreList?.error ?? "매장 목록을 불러올 수 없습니다."}
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="주문 취소"
      description="결제사 환불 & DB 수정 & 쿠폰 취소 올인원 가능. 매장 선택 후 주문을 선택하고 취소 사유를 입력하세요."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={storeList.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = storeList.find((x) => x.id === store.id);
              if (full) handleSelectStore(full);
            }}
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "주문 취소",
        emptyText: "왼쪽에서 매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            {ordersLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinnerInline />
              </div>
            ) : (
              <>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">선택 매장:</span>{" "}
                  {selectedStore.title}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          주문자
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          주문일시
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          주문금액
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          상태
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          선택
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                          {orders.map((order: OrdersByStoreIdQuery["ordersByStoreId"][number]) => (
                        <tr key={order.id}>
                          <td className="px-2 py-2">
                            {order.user?.name} ({order.user?.phone})
                          </td>
                          <td className="px-2 py-2">
                            {order.createdAt
                              ? new Date(order.createdAt).toLocaleString()
                              : "—"}
                          </td>
                          <td className="px-2 py-2">{order.amount}</td>
                          <td className="px-2 py-2">
                            <span
                              className={clsx(
                                "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                                order.isCanceled
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800",
                              )}
                            >
                              {order.isCanceled ? "취소" : "결제완료"}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              onClick={() => setSelectedOrderId(order.id)}
                              className={clsx(
                                "p-2 rounded text-sm",
                                selectedOrderId === order.id
                                  ? "bg-gray-500 text-white"
                                  : "bg-blue-500 text-white hover:bg-blue-600",
                              )}
                            >
                              선택
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-4 bg-gray-100 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    취소 사유 (4글자 이상, 10자 이내 · 사장님에게 노출)
                  </label>
                  <input
                    type="text"
                    value={cancelReason}
                    placeholder="취소 사유 입력"
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="border p-2 rounded w-full text-sm"
                  />
                </div>

                {selectedOrderId && (
                  <button
                    type="button"
                    onClick={() => handleCancelOrder(selectedOrderId)}
                    className="w-full py-3 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600"
                  >
                    주문 취소
                  </button>
                )}
              </>
            )}
          </div>
        ) : null,
      }}
    />
  );
}
